/**
 * Doulas — the panel, and one doula at a time.
 *
 * Two states on one page rather than two routes. A supervisor opening this
 * arrives either to find somebody or to check on somebody they already have in
 * mind, and a separate detail route would make the second case a navigation
 * problem. Selecting a row reveals her; the panel stays beside it.
 *
 * THE PANEL IS ORDERED BY AVAILABILITY, not alphabetically — the server does
 * that, and the list repeats it visually so the ordering reads as intentional
 * rather than arbitrary.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetchJson } from '@/lib/authFetch';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CreditCard, Users } from 'lucide-react';
import StaffDoulaPanel from '@/components/doulas/StaffDoulaPanel';
import MatchConsole from '@/components/doulas/MatchConsole';

interface StaffDoulaSummary {
  panel_id: number;
  display_name: string;
  status: string;
  accepting_new_clients: boolean;
  active_clients: number;
  caseload_ceiling: number | null;
  has_card: boolean;
  languages: string[];
}

export default function DoulasPage() {
  const { t } = useTranslation();
  const [panel, setPanel] = useState<StaffDoulaSummary[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  // Two questions, not one screen: who is on the panel, and who is waiting on
  // an answer. The second had no surface at all before TRUS-679.
  const [view, setView] = useState<'panel' | 'waiting'>('panel');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await authFetchJson<StaffDoulaSummary[]>('/api/doulas');
      setPanel(rows);
      // Open the first one, so the page shows what it does rather than an empty
      // right-hand side waiting to be clicked.
      setSelected((cur) => cur ?? (rows.length > 0 ? rows[0].panel_id : null));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('doulasPage.loadFailed', 'We could not load the panel.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <PageState variant="loading" />;

  return (
    <PageContainer>
      <PageHeader
        title={t('doulasPage.title', 'Doulas')}
        description={t('doulasPage.description', 'Your panel — who is available, what they have been paid, and what they have delivered.')}
      />

      <div className="mb-4 flex gap-2">
        <button
          type="button"
          onClick={() => setView('panel')}
          className={
            view === 'panel'
              ? 'rounded-md border border-primary bg-accent/40 px-3 py-1.5 text-sm font-medium'
              : 'rounded-md border px-3 py-1.5 text-sm hover:bg-accent/20'
          }
        >
          {t('doulasPage.tabPanel', 'The panel')}
        </button>
        <button
          type="button"
          onClick={() => setView('waiting')}
          className={
            view === 'waiting'
              ? 'rounded-md border border-primary bg-accent/40 px-3 py-1.5 text-sm font-medium'
              : 'rounded-md border px-3 py-1.5 text-sm hover:bg-accent/20'
          }
        >
          {t('doulasPage.tabWaiting', 'Waiting on an answer')}
        </button>
      </div>

      {view === 'waiting' && <MatchConsole />}

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {view === 'panel' && (panel.length === 0 ? (
        <div className="rounded-md border p-8 text-center">
          <Users className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">
            {t('doulasPage.empty', 'No doulas on your panel yet.')}
          </p>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
          <ul className="flex flex-col gap-2">
            {panel.map((d) => {
              const isOpen = d.panel_id === selected;
              return (
                <li key={d.panel_id}>
                  <button
                    type="button"
                    onClick={() => setSelected(d.panel_id)}
                    aria-current={isOpen ? 'true' : undefined}
                    className={
                      isOpen
                        ? 'w-full rounded-md border border-primary bg-accent/40 p-3 text-left'
                        : 'w-full rounded-md border p-3 text-left hover:bg-accent/20'
                    }
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{d.display_name}</span>
                      {d.has_card && (
                        <CreditCard
                          className="h-3.5 w-3.5 text-muted-foreground"
                          aria-label={t('doulasPage.hasCard', 'has a card')}
                        />
                      )}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        {t('doulasPage.clients', '{{count}} clients', { count: d.active_clients })}
                        {d.caseload_ceiling ? ` / ${d.caseload_ceiling}` : ''}
                      </span>
                      {/* Availability, not just status — it is why the list is
                          ordered the way it is. */}
                      <Badge variant={d.accepting_new_clients ? 'success' : 'secondary'}>
                        {d.accepting_new_clients
                          ? t('doulasPage.accepting', 'Taking clients')
                          : t('doulasPage.full', 'Full')}
                      </Badge>
                      {d.status !== 'contracted' && <Badge variant="warning">{d.status}</Badge>}
                    </span>
                    {d.languages.length > 0 && (
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {d.languages.join(', ')}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          <div>{selected !== null && <StaffDoulaPanel panelId={selected} />}</div>
        </div>
      ))}
    </PageContainer>
  );
}
